import React, { useState, useCallback } from 'react';
import { Upload, FileText, CheckCircle, XCircle, AlertTriangle, Loader2, Database, Eye, Files } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;


const Karat307ImportTab = () => {
    const [files, setFiles] = useState([]);
    const [preview, setPreview] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [deviceName, setDeviceName] = useState('');
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [parsing, setParsing] = useState(false);
    const [dragActive, setDragActive] = useState(false);

    const loadDevices = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/admin/karat307/devices`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setDevices(data);
            }
        } catch (err) {
            console.error('Ошибка загрузки устройств:', err);
        }
    }, []);

    React.useEffect(() => { loadDevices(); }, [loadDevices]);

    const handleFileSelect = (fileList) => {
        if (!fileList || fileList.length === 0) return;
        const fileArray = Array.from(fileList);
        const invalidFiles = fileArray.filter(f => !f.name.toLowerCase().endsWith('.csv'));
        if (invalidFiles.length > 0) {
            alert('Все файлы должны быть в формате CSV');
            return;
        }
        setFiles(fileArray);
        setPreview(null);
        setImportResult(null);
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
        else if (e.type === 'dragleave') setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileSelect(e.dataTransfer.files);
        }
    };

    const handlePreview = async () => {
        if (files.length === 0) return;
        setParsing(true);
        setPreview(null);

        const formData = new FormData();
        files.forEach(f => formData.append('files', f));

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/admin/karat307/parse-archive`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });
            const data = await res.json();
            if (res.ok) {
                setPreview(data);
                if (data.serialNumber && !deviceName) {
                    const match = devices.find(d =>
                        d.name.includes(data.serialNumber) ||
                        (d.description && d.description.includes(data.serialNumber))
                    );
                    if (match) setDeviceName(match.name);
                }
            } else {
                alert(data.message || 'Ошибка парсинга');
            }
        } catch (err) {
            console.error(err);
            alert('Ошибка соединения с сервером');
        } finally {
            setParsing(false);
        }
    };

    const handleImport = async () => {
        if (files.length === 0) return;
        setLoading(true);
        setImportResult(null);

        const formData = new FormData();
        files.forEach(f => formData.append('files', f));

        const url = deviceName
            ? `${API_URL}/api/admin/karat307/import-archive?deviceName=${encodeURIComponent(deviceName)}`
            : `${API_URL}/api/admin/karat307/import-archive`;

        try {
            const token = localStorage.getItem('token');
            const res = await fetch(url, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });
            const data = await res.json();
            setImportResult(data);
        } catch (err) {
            console.error(err);
            setImportResult({ success: false, message: 'Ошибка соединения с сервером' });
        } finally {
            setLoading(false);
        }
    };

    // ✅ ФИЛЬТРУЕМ колонки ошибок и наработки — они отображаются в отдельных колонках
    const dataColumns = (preview?.columns || []).filter(col =>
        !col.includes('Ошибки') && !col.includes('Наработка')
    );

    // Определяем, есть ли в файле колонки ГВС
    const hasGvs = dataColumns.some(col =>
        col.includes('T3') || col.includes('T4') ||
        col.includes('Q4') || col.includes('Q5') ||
        col.includes('G3') || col.includes('G4')
    );

    // Определяем, есть ли ГВС2
    const hasGvs2 = (preview?.columns || []).some(col =>
        col.includes('ГВС2') || col.includes('Tmin 3') || col.includes('Tmax 3') ||
        col.includes('Q5min') || col.includes('Q6')
    );

    return (
        <div className="space-y-6">
            {/* Зона загрузки */}
            <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${dragActive ? 'border-blue-400 bg-blue-500/10' : 'border-slate-600 hover:border-slate-500'
                    }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
            >
                <Files className="w-10 h-10 text-slate-500 mx-auto mb-3" />
                <p className="text-slate-300 mb-2">
                    Перетащите CSV-файлы архива и конфигурации Карат-307 сюда
                </p>
                <p className="text-slate-500 text-sm mb-4">
                    Поддерживается загрузка одного или двух файлов (CFG + архив)
                </p>
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg cursor-pointer transition">
                    <FileText className="w-4 h-4" />
                    Выбрать файлы
                    <input
                        type="file"
                        accept=".csv"
                        multiple
                        className="hidden"
                        onChange={(e) => handleFileSelect(e.target.files)}
                    />
                </label>

                {files.length > 0 && (
                    <div className="mt-4 space-y-2">
                        {files.map((f, idx) => (
                            <div key={idx} className="flex items-center justify-center gap-2 text-green-400 text-sm">
                                <CheckCircle className="w-4 h-4" />
                                <span>{f.name} ({(f.size / 1024).toFixed(1)} КБ)</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Выбор устройства */}
            {files.length > 0 && (
                <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                    <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                        <Database className="w-4 h-4 text-blue-400" />
                        Устройство для импорта
                    </h4>
                    <select
                        value={deviceName}
                        onChange={(e) => setDeviceName(e.target.value)}
                        className="w-full bg-slate-700 text-white rounded-lg px-3 py-2 border border-slate-600 focus:border-blue-500 focus:outline-none"
                    >
                        <option value="">— Автоопределение по серийному номеру —</option>
                        {devices.map((d) => (
                            <option key={d.name} value={d.name}>
                                {d.name} — {d.city}, {d.street} {d.houseNumber}
                            </option>
                        ))}
                    </select>
                    {preview?.serialNumber && (
                        <p className="text-slate-400 text-sm mt-2">
                            Серийный номер из файла: <span className="text-white font-mono">{preview.serialNumber}</span>
                        </p>
                    )}
                </div>
            )}

            {/* Кнопки действий */}
            {files.length > 0 && (
                <div className="flex gap-3">
                    <button
                        onClick={handlePreview}
                        disabled={parsing}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition disabled:opacity-50"
                    >
                        {parsing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                        Предпросмотр
                    </button>
                    <button
                        onClick={handleImport}
                        disabled={loading || !deviceName}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                        title={!deviceName ? 'Выберите устройство' : ''}
                    >
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        Импортировать в БД
                    </button>
                </div>
            )}

            {/* Результат предпросмотра */}
            {preview && (
                <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
                    <h4 className="text-white font-medium mb-3">
                        Предпросмотр: {preview.archiveType || 'Архив'}
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
                        <div className="bg-slate-700/50 rounded-lg p-2">
                            <span className="text-slate-400">Прибор:</span>
                            <p className="text-white">{preview.deviceType}</p>
                        </div>
                        <div className="bg-slate-700/50 rounded-lg p-2">
                            <span className="text-slate-400">Серийный №:</span>
                            <p className="text-white font-mono">{preview.serialNumber}</p>
                        </div>
                        <div className="bg-slate-700/50 rounded-lg p-2">
                            <span className="text-slate-400">Тип архива:</span>
                            <p className="text-white">{preview.archiveType}</p>
                        </div>
                        <div className="bg-slate-700/50 rounded-lg p-2">
                            <span className="text-slate-400">Записей:</span>
                            <p className="text-white font-bold">{preview.totalRows}</p>
                        </div>
                    </div>

                    {/* ✅ ИСПРАВЛЕННАЯ таблица предпросмотра */}
                    {preview.preview && preview.preview.length > 0 && (
                        <div className="overflow-auto max-h-[500px] border border-slate-700 rounded-lg">
                            <table className="w-full text-xs text-left whitespace-nowrap">
                                <thead className="sticky top-0 bg-slate-800 z-10">
                                    <tr className="border-b border-slate-600">
                                        <th className="px-2 py-1.5 text-slate-400 sticky left-0 bg-slate-800 z-20 shadow-sm font-medium">
                                            Дата
                                        </th>
                                        {dataColumns.map(col => (
                                            <th key={col} className="px-2 py-1.5 text-slate-400 font-medium">
                                                {col}
                                            </th>
                                        ))}
                                        {/* ✅ Отопление — ошибки и наработка */}
                                        <th className="px-2 py-1.5 text-green-400 font-medium">Ошибки</th>
                                        <th className="px-2 py-1.5 text-green-400 font-medium">Нараб.</th>
                                        {/* ✅ ГВС1 */}
                                        {hasGvs && (
                                            <>
                                                <th className="px-2 py-1.5 text-blue-400 font-medium">Ош. ГВС1</th>
                                                <th className="px-2 py-1.5 text-blue-400 font-medium">Нар. ГВС1</th>
                                            </>
                                        )}
                                        {/* ✅ ГВС2 */}
                                        {hasGvs2 && (
                                            <>
                                                <th className="px-2 py-1.5 text-purple-400 font-medium">Ош. ГВС2</th>
                                                <th className="px-2 py-1.5 text-purple-400 font-medium">Нар. ГВС2</th>
                                            </>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {preview.preview.map((row, idx) => (
                                        <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                                            <td className="px-2 py-1 text-white sticky left-0 bg-slate-800 z-10 shadow-sm font-medium">
                                                {row.date}
                                            </td>
                                            {dataColumns.map(col => {
                                                const val = row.values?.[col];
                                                return (
                                                    <td key={col} className="px-2 py-1 text-slate-300">
                                                        {val !== undefined && val !== null
                                                            ? Number(val).toFixed(3)
                                                            : '—'}
                                                    </td>
                                                );
                                            })}
                                            {/* ✅ Отопление — ошибки и наработка */}
                                            <td className="px-2 py-1">
                                                <span className={
                                                    (row.errorsText || 'Ok').toLowerCase() === 'ok'
                                                        ? 'text-green-400'
                                                        : 'text-red-400'
                                                }>
                                                    {row.errorsText || 'Ok'}
                                                </span>
                                            </td>
                                            <td className="px-2 py-1 text-slate-300">
                                                {row.narabotka !== undefined && row.narabotka !== null
                                                    ? Number(row.narabotka).toFixed(1) + ' ч'
                                                    : '—'}
                                            </td>
                                            {/* ✅ ГВС1 */}
                                            {hasGvs && (
                                                <>
                                                    <td className="px-2 py-1">
                                                        <span className={
                                                            !row.errorsGvsText || (row.errorsGvsText || 'Ok').toLowerCase() === 'ok'
                                                                ? 'text-green-400'
                                                                : 'text-orange-400'
                                                        }>
                                                            {row.errorsGvsText || 'Ok'}
                                                        </span>
                                                    </td>
                                                    <td className="px-2 py-1 text-slate-300">
                                                        {row.narabotkaGvs !== undefined && row.narabotkaGvs !== null
                                                            ? Number(row.narabotkaGvs).toFixed(1) + ' ч'
                                                            : '—'}
                                                    </td>
                                                </>
                                            )}
                                            {/* ✅ ГВС2 */}
                                            {hasGvs2 && (
                                                <>
                                                    <td className="px-2 py-1">
                                                        <span className={
                                                            !row.errorsGvs2Text || (row.errorsGvs2Text || 'Ok').toLowerCase() === 'ok'
                                                                ? 'text-green-400'
                                                                : 'text-red-400'
                                                        }>
                                                            {row.errorsGvs2Text || 'Ok'}
                                                        </span>
                                                    </td>
                                                    <td className="px-2 py-1 text-slate-300">
                                                        {row.narabotkaGvs2 !== undefined && row.narabotkaGvs2 !== null
                                                            ? Number(row.narabotkaGvs2).toFixed(1) + ' ч'
                                                            : '—'}
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Результат импорта */}
            {importResult && (
                <div className={`rounded-xl p-4 border ${importResult.success ? 'bg-green-900/20 border-green-700' : 'bg-red-900/20 border-red-700'
                    }`}>
                    <div className="flex items-start gap-3">
                        {importResult.success
                            ? <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                            : <XCircle className="w-5 h-5 text-red-400 mt-0.5" />
                        }
                        <div>
                            <h4 className={`font-medium ${importResult.success ? 'text-green-300' : 'text-red-300'}`}>
                                {importResult.success ? 'Импорт завершён' : 'Ошибка импорта'}
                            </h4>
                            <p className="text-slate-300 text-sm mt-1">{importResult.message}</p>

                            {importResult.success && (
                                <div className="grid grid-cols-3 gap-4 mt-3 text-center">
                                    <div className="bg-green-800/30 rounded-lg p-2">
                                        <p className="text-2xl font-bold text-green-400">{importResult.importedRows}</p>
                                        <p className="text-xs text-slate-400">Добавлено</p>
                                    </div>
                                    <div className="bg-yellow-800/30 rounded-lg p-2">
                                        <p className="text-2xl font-bold text-yellow-400">{importResult.skippedRows}</p>
                                        <p className="text-xs text-slate-400">Пропущено</p>
                                    </div>
                                    <div className="bg-red-800/30 rounded-lg p-2">
                                        <p className="text-2xl font-bold text-red-400">{importResult.errorRows}</p>
                                        <p className="text-xs text-slate-400">С ошибками</p>
                                    </div>
                                </div>
                            )}

                            {importResult.errors && importResult.errors.length > 0 && (
                                <div className="mt-3">
                                    <p className="text-sm text-red-400 font-medium flex items-center gap-1">
                                        <AlertTriangle className="w-4 h-4" />
                                        Ошибки по строкам:
                                    </p>
                                    <ul className="mt-1 text-xs text-red-300 space-y-0.5 max-h-32 overflow-y-auto">
                                        {importResult.errors.map((err, i) => (
                                            <li key={i}>• {err}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Karat307ImportTab;